import React, { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { blogPosts, BlogPost } from '@/lib/blogData';
import { Button } from '@/components/ui/button';
import MarkdownRenderer from '@/components/blog/MarkdownRenderer';
import BlogPostSection from '@/components/blog/BlogPostSection';
import { BlogPostHeader } from '@/components/blog/post/BlogPostHeader';
import { BlogPostFooter } from '@/components/blog/post/BlogPostFooter';
import { RelatedPosts } from '@/components/blog/post/RelatedPosts';
import { BlogSidebar } from '@/components/blog/BlogSidebar';
import SEO from '@/components/seo/SEO';

const BlogPostPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogPost | null>(null);

  useEffect(() => {
    const foundPost = blogPosts.find(p => p.slug === slug);
    setPost(foundPost || null);
    window.scrollTo(0, 0);
  }, [slug]);

  const { intro, sections, readTime } = useMemo(() => {
    if (!post) return { intro: '', sections: [], readTime: 0 };
    
    const wordCount = post.contentMarkdown.split(/\s+/).length;
    const time = Math.ceil(wordCount / 200); 

    // Split only at level-two headings; matching inside ### corrupts subheadings.
    const safeParts = post.contentMarkdown.split(/(?=^## )/m);
    
    return { intro: safeParts[0], sections: safeParts.slice(1), readTime: time };
  }, [post]);

  if (!post) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <h1 className="text-2xl font-bold mb-4">Article not found</h1>
        <Button asChild><Link to="/blog">Return to Blog</Link></Button>
      </div>
    );
  }

  const ogImage = post.imageUrl;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://runofftrack.com/blog/${post.slug}`
    },
    "headline": post.title,
    "image": ogImage ? [ogImage] : ["https://runofftrack.com/og-image.png"],
    "datePublished": post.date,
    "dateModified": post.modifiedDate || post.date,
    "author": {
      "@type": "Organization",
      "name": "RunOffTrack",
      "url": "https://runofftrack.com"
    },
    "publisher": {
      "@type": "Organization",
      "name": "RunOffTrack",
      "logo": {
        "@type": "ImageObject",
        "url": "https://runofftrack.com/assets/logo.svg"
      }
    },
    "description": post.excerpt
  };

  return (
    <div className="min-h-screen bg-background font-sans selection:bg-primary/20">
      <SEO 
        title={post.title}
        description={post.excerpt}
        canonical={`/blog/${post.slug}`}
        ogType="article"
        ogImage={ogImage}
        articlePublishedTime={post.date}
        articleModifiedTime={post.modifiedDate || post.date}
        articleAuthor="RunOffTrack"
        articleSection={post.category}
        articleTags={post.tags}
        jsonLd={jsonLd}
        noindex={post.indexable === false}
      />

      <article>
        <BlogPostHeader post={post} readTime={readTime} />

        <div className="container px-4 py-12 md:py-20 mx-auto">
            {/* Header Image */}
            {post.imageUrl && (
                <div className="relative -mt-20 md:-mt-32 mb-16 rounded-2xl overflow-hidden shadow-2xl border border-border bg-card max-w-5xl mx-auto">
                    <img src={post.imageUrl} alt={post.title} className="w-full h-auto object-cover aspect-[21/9]" />
                </div>
            )}

            <div className="grid lg:grid-cols-12 gap-12 max-w-6xl mx-auto">
                {/* Main Content Column */}
                <div className="lg:col-span-8">
                    <div className="mb-12">
                        <MarkdownRenderer content={intro} />
                    </div>

                    {sections.map((section, idx) => (
                        <BlogPostSection key={idx}>
                            <MarkdownRenderer content={section} />
                        </BlogPostSection>
                    ))}

                    <BlogPostFooter tags={post.tags} />
                </div>

                {/* Sidebar Column */}
                <aside className="lg:col-span-4 space-y-8">
                    <div className="sticky top-24">
                        <BlogSidebar />
                    </div>
                </aside>
            </div>
        </div>
      </article>

      <RelatedPosts currentSlug={post.slug} />
    </div>
  );
};

export default BlogPostPage;
